import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import flagFontUrl from "country-flag-emoji-polyfill/dist/TwemojiCountryFlags.woff2";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Loader2 } from "lucide-react";
import {
  StrictMode,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import ReactDOM from "react-dom/client";

import { createAppRouter } from "@/app";
import { AppToaster } from "@/components/ui/app-toaster";
import { Button } from "@/components/ui/button";
import { appConfig, getAccessToken } from "@/config";
import { LanguageProvider } from "@/contexts/language";
import { initTheme } from "@/contexts/theme";
import { getLanguage, setLanguage, translate } from "@/i18n";
import { initNativeChrome } from "@/lib/capacitor";
import { initSound } from "@/lib/sound";
import { errorMessage } from "@/lib/errors";
import { loadSession } from "@/session";

import "@/main.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createAppRouter();

// Google Maps context for the location picker/previews — only mounted when a
// key is configured, so the app never loads the Maps JS API without one.
function MaybeMapsProvider({ children }: { children: ReactNode }) {
  if (!appConfig.googleMapsApiKey) return <>{children}</>;
  return (
    <APIProvider apiKey={appConfig.googleMapsApiKey}>{children}</APIProvider>
  );
}

// Windows Chromium draws flag emoji as letter pairs ("SA"); this injects a
// flags-only fallback font (bundled, self-hosted) on platforms that need it.
// "Twemoji Country Flags" leads the --font-sans stack in main.css.
polyfillCountryFlagEmojis("Twemoji Country Flags", flagFontUrl);
// Theme before first paint (the index.html inline script already did a first
// pass; this arms the matchMedia listener and meta sync).
initTheme();
// Native shell chrome (status bar / keyboard / splash) — fire and forget.
void initNativeChrome();
// Browsers only let audio play after a gesture; prime the chime on the first one.
initSound();

function BootSpinner() {
  return (
    <div className="tw:flex tw:min-h-dvh tw:items-center tw:justify-center">
      <Loader2
        className="tw:size-6 tw:animate-spin tw:text-muted-foreground"
        strokeWidth={1.75}
      />
    </div>
  );
}

function BootError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="tw:flex tw:min-h-dvh tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:p-8 tw:text-center">
      <p className="tw:m-0 tw:text-lg tw:font-semibold">
        {translate("Mandoob:Boot.SessionFailed")}
      </p>
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">{message}</p>
      <Button variant="outline" onClick={onRetry} className="tw:mt-2">
        {translate("Mandoob:Boot.Retry")}
      </Button>
    </div>
  );
}

function Boot() {
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready" }
    | { status: "error"; message: string }
  >({ status: "loading" });

  const boot = useCallback(() => {
    setState({ status: "loading" });
    // Apply the stored/detected language immediately so the loading state is
    // in the right direction, then let the session's preference win on first
    // boot only (no stored preference yet).
    setLanguage(getLanguage(), false);

    // Signed out: render the router anyway — its guards redirect to /login.
    if (!getAccessToken()) {
      setState({ status: "ready" });
      return;
    }

    loadSession()
      .then((session) => {
        try {
          if (!localStorage.getItem("mandoob:language")) {
            setLanguage(session.language, false);
          }
        } catch {
          // storage unavailable
        }
        setState({ status: "ready" });
      })
      .catch((error) => {
        // The 401 interceptor already clears the token and redirects to
        // /login; anything else is a real boot failure.
        setState({
          status: "error",
          message: errorMessage(error, translate, "Mandoob:Boot.SessionFailed"),
        });
      });
  }, []);

  useEffect(() => {
    boot();
  }, [boot]);

  if (state.status === "loading") {
    return <BootSpinner />;
  }
  if (state.status === "error") {
    return <BootError message={state.message} onRetry={boot} />;
  }
  return <RouterProvider router={router} />;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <MaybeMapsProvider>
          <Boot />
          <AppToaster />
        </MaybeMapsProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </StrictMode>,
);
